import { useState } from 'react'
import { FileX, MapPin, Building2, Users, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react'
import {
  Bar, BarChart, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell,
  LineChart, Line, CartesianGrid,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

// ── Tipos ──────────────────────────────────────────────────────────────────

interface GrupoItem {
  nombre: string
  facturas: number
  monto: number
}

interface FechaItem {
  fecha: string
  facturas: number
  monto: number
}

interface Factura {
  numero: string
  fecha_emision: string
  establecimiento: string
  razon_social: string
  ciudad: string
  provincia: string
  representante: string
  monto: number
}

interface ExcluidasData {
  periodo: string
  total_facturas: number
  monto_total: number
  monto_promedio: number
  por_representante: GrupoItem[]
  por_provincia: GrupoItem[]
  por_ciudad: GrupoItem[]
  por_razon_social: GrupoItem[]
  por_fecha: FechaItem[]
  facturas: Factura[]
}

type Metrica = 'facturas' | 'monto'

// ── Constantes ──────────────────────────────────────────────────────────────

const BLUE = '#6a5cbc'
const BLUE_LIGHT = '#6a5cbc18'
const SHADES = ['#6a5cbc', '#7d71c9', '#9186d6', '#a49be3', '#c4bef0']
const MAX_CHART_HEIGHT = 400

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 0): string {
  return n.toLocaleString('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function shade(idx: number, total: number): string {
  const ratio = total <= 1 ? 0 : idx / (total - 1)
  const i = Math.round(ratio * (SHADES.length - 1))
  return SHADES[i]
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

function formatMetrica(v: number, metrica: Metrica): string {
  return metrica === 'monto' ? `S/ ${fmt(v, 0)}` : `${fmt(v)}`
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon,
}: {
  label: string; value: string; sub?: string; icon: React.ElementType
}) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card h-full"
      style={{ backgroundImage: `linear-gradient(to right, ${BLUE_LIGHT}, transparent 60%)` }}
    >
      <div className="h-full px-4 py-4 flex flex-col justify-center gap-2">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <Icon className="h-9 w-9" style={{ color: BLUE }} />
          </div>
          <div className="flex-1 min-w-0 text-right">
            <div className="text-3xl font-bold text-foreground leading-tight">{value}</div>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Selector de métrica (ancho completo) ──────────────────────────────────────

function MetricaSelector({
  metrica,
  onChange,
}: {
  metrica: Metrica
  onChange: (m: Metrica) => void
}) {
  const opciones: { key: Metrica; label: string }[] = [
    { key: 'facturas', label: 'Cantidad de facturas' },
    { key: 'monto', label: 'Valor en soles (S/)' },
  ]
  return (
    <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-full">
      {opciones.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className="flex-1 py-2.5 text-sm font-medium transition-colors"
          style={
            metrica === key
              ? { backgroundColor: BLUE, color: '#fff' }
              : { backgroundColor: 'transparent', color: BLUE }
          }
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ── Tooltip grupo ─────────────────────────────────────────────────────────────

function GrupoTooltip({ active, payload, label, metrica }: {
  active?: boolean
  payload?: { value: number }[]
  label?: string
  metrica: Metrica
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className="text-muted-foreground">{formatMetrica(payload[0].value, metrica)}</p>
    </div>
  )
}

// ── Gráfica de grupo ──────────────────────────────────────────────────────────

function GrupoChart({
  title, data, metrica, icon: Icon,
}: {
  title: string
  data: GrupoItem[]
  metrica: Metrica
  icon: React.ElementType
}) {
  const sorted = [...data].sort((a, b) => b[metrica] - a[metrica])
  const barHeight = 44
  const chartHeight = Math.max(sorted.length * barHeight + 20, 120)

  return (
    <Card className="border border-gray-200 dark:border-gray-700">
      <CardHeader className="pb-2 flex flex-row items-center gap-2 space-y-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</CardTitle>
        <span
          className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ backgroundColor: `${BLUE}18`, color: BLUE }}
        >
          {sorted.length}
        </span>
      </CardHeader>
      <CardContent className="p-0 pb-3">
        <div className="overflow-y-auto px-6" style={{ maxHeight: MAX_CHART_HEIGHT }}>
          <div style={{ height: chartHeight, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sorted} layout="vertical" margin={{ left: 0, right: 80, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis
                  dataKey="nombre"
                  type="category"
                  tickLine={false}
                  axisLine={false}
                  width={160}
                  tick={{ fontSize: 10 }}
                  interval={0}
                />
                <Tooltip content={<GrupoTooltip metrica={metrica} />} cursor={{ fill: `${BLUE}10` }} />
                <Bar dataKey={metrica} radius={[0, 4, 4, 0]}>
                  {sorted.map((_, idx) => (
                    <Cell key={idx} fill={shade(idx, sorted.length)} />
                  ))}
                  <LabelList
                    dataKey={metrica}
                    position="right"
                    style={{ fontSize: 11, fill: '#6b7280' }}
                    formatter={(v: number) => formatMetrica(v, metrica)}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Gráfica de línea por fecha ────────────────────────────────────────────────

function FechaLineChart({ data, metrica }: { data: FechaItem[]; metrica: Metrica }) {
  const display = data.map(d => ({ ...d, label: shortDate(d.fecha) }))

  return (
    <Card className="border border-gray-200 dark:border-gray-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {metrica === 'monto' ? 'Valor por día de carga (S/)' : 'Facturas por día de carga'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={display} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={50} tickFormatter={v => metrica === 'monto' ? `${fmt(v/1000, 0)}k` : String(v)} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                return (
                  <div className="bg-white dark:bg-card border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
                    <p className="font-semibold text-foreground mb-1">{label}</p>
                    <p className="text-muted-foreground">{formatMetrica(payload[0].value as number, metrica)}</p>
                  </div>
                )
              }}
            />
            <Line
              type="monotone"
              dataKey={metrica}
              stroke={BLUE}
              strokeWidth={2}
              dot={{ r: 3, fill: BLUE }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── Página principal ─────────────────────────────────────────────────────────

export function MediafarmaPeruSepExcluidasPage() {
  useDocumentTitle('Facturas Excluidas Septiembre · Medifarma Perú')

  const { data, isLoading, isError } = useDashReport<ExcluidasData>('medifarma-peru/septiembre/facturas-excluidas')
  const [metrica, setMetrica] = useState<Metrica>('facturas')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const sortedFacturas = [...(data?.facturas ?? [])].sort((a, b) =>
    sortDir === 'desc' ? b.monto - a.monto : a.monto - b.monto
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BLUE }} />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Error al cargar los datos.
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Facturas Excluidas — Establecimientos No Participantes
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {data.periodo} · Programa de Fidelización Medifarma Perú
                </p>
              </div>
            </div>
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxC1wKpIiaTpYQ9UIOCvWwoUZDLGpSxWvaR5x1cEGXyYaSxkCDU6SkRHDZ&s=10"
              alt="Medifarma Peru"
              className="h-12 w-auto flex-shrink-0 object-contain"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Contexto ── */}
      <Card className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-card">
        <CardContent className="py-4 px-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Facturas subidas por dependientes afiliados a{' '}
            <span className="font-semibold text-foreground">establecimientos no incluidos</span>{' '}
            en el programa. Estas facturas fueron <span className="font-semibold text-foreground">rechazadas</span> y
            no generan puntos válidos. El análisis permite identificar patrones de farmacias fuera del programa
            y evaluar su potencial incorporación.
          </p>
        </CardContent>
      </Card>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={FileX}
          label="Total facturas excluidas"
          value={fmt(data.total_facturas)}
          sub={data.periodo}
        />
        <KpiCard
          icon={TrendingUp}
          label="Valor total excluido"
          value={`S/ ${fmt(data.monto_total, 0)}`}
          sub={`Promedio S/ ${fmt(data.monto_promedio, 2)} por factura`}
        />
        <KpiCard
          icon={Users}
          label="Representantes involucrados"
          value={fmt(data.por_representante.length)}
          sub="Con facturas rechazadas"
        />
        <KpiCard
          icon={Building2}
          label="Establecimientos distintos"
          value={fmt(data.por_razon_social.length)}
          sub="Razones sociales únicas"
        />
      </div>

      {/* ── Selector de métrica ── */}
      <MetricaSelector metrica={metrica} onChange={setMetrica} />

      {/* ── Gráfica línea por fecha ── */}
      <FechaLineChart data={data.por_fecha} metrica={metrica} />

      {/* ── Gráficas 2×2 ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GrupoChart title="Por representante" data={data.por_representante} metrica={metrica} icon={Users} />
        <GrupoChart title="Por razón social" data={data.por_razon_social} metrica={metrica} icon={Building2} />
        <GrupoChart title="Por provincia" data={data.por_provincia} metrica={metrica} icon={MapPin} />
        <GrupoChart title="Por ciudad / distrito" data={data.por_ciudad} metrica={metrica} icon={MapPin} />
      </div>

      {/* ── Tabla de detalle ── */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Detalle de facturas excluidas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Establecimiento</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Razón Social</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ciudad</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Provincia</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Representante</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <button
                      onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                      className="inline-flex items-center gap-1 hover:opacity-70 transition-opacity"
                    >
                      Monto S/
                      {sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedFacturas.map((f, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{f.fecha_emision}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground">{f.establecimiento}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground">{f.razon_social}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{f.ciudad}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{f.provincia}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground">{f.representante.split(' ').slice(0, 3).join(' ')}</td>
                    <td className="px-4 py-2.5 text-xs text-right font-medium text-foreground">
                      {f.monto > 0 ? `S/ ${fmt(f.monto, 2)}` : <span className="text-muted-foreground/50">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 font-semibold">
                  <td colSpan={6} className="px-4 py-2.5 text-xs text-muted-foreground uppercase tracking-wide">
                    Total — {fmt(data.total_facturas)} facturas
                  </td>
                  <td className="px-4 py-2.5 text-xs text-right text-foreground">
                    S/ {fmt(data.monto_total, 2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground/40 pb-2">
        Tonal Strategies · {data.periodo} · Establecimientos no participantes
      </p>
    </div>
  )
}
